/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { trackKeyUsage, isValidTranslationKey } from "helpers/trackUsedKeys";

// Queue of missing keys per page, flushed once Sentry is available
const pendingKeys = new Map<string, Set<string>>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const isSentryReady = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.Sentry !== "undefined" &&
  typeof window.Sentry.captureMessage === "function";

const flushToSentry = (): void => {
  if (!isSentryReady()) return;

  // Track reported keys on window to avoid re-reporting across navigations
  if (!(window as any).__reportedKeysToSentry) {
    (window as any).__reportedKeysToSentry = new Map<string, Set<string>>();
  }
  const reportedMap = (window as any).__reportedKeysToSentry as Map<string, Set<string>>;

  pendingKeys.forEach((keys, pageName) => {
    if (keys.size === 0) return;

    // Filter out already reported keys for this page
    if (!reportedMap.has(pageName)) {
      reportedMap.set(pageName, new Set<string>());
    }
    const alreadyReported = reportedMap.get(pageName);
    if (!alreadyReported) return;
    const newKeys = Array.from(keys).filter((k) => !alreadyReported.has(k));

    if (newKeys.length === 0) return;

    // Mark as reported
    newKeys.forEach((k) => alreadyReported.add(k));

    window.Sentry.captureMessage("Critical: Missing Dot-Formatted Translation Keys", {
      level: "error",
      fingerprint: ["missing-translation-keys", pageName, newKeys.join(",")],
      tags: {
        page: pageName,
        missingKeyCount: String(newKeys.length),
        severity: "critical",
      },
      extra: {
        type: "MISSING_DOTFORMATTED_TRANSLATION_KEYS",
        page: pageName,
        missingDotFormattedKeys: newKeys,
        missingKeyCount: newKeys.length,
        timestamp: new Date().toISOString(),
      },
    });
  });
  pendingKeys.clear();
};

let listenerAttached = false;

const scheduleFlush = (): void => {
  // If Sentry is already loaded, flush immediately (debounced)
  if (isSentryReady()) {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushToSentry();
    }, 500);
    return;
  }

  // Otherwise, listen for the sentryReady event (once)
  if (!listenerAttached) {
    listenerAttached = true;
    window.addEventListener("sentryReady", () => flushToSentry(), { once: true });
  }
};

// Report missing key — queues it and schedules a batched flush to Sentry
const reportMissingKey = (key: string, pageName: string): void => {
  if (typeof window === "undefined") return;
  if (!isValidTranslationKey(key, true)) return;

  if (!pendingKeys.has(pageName)) {
    pendingKeys.set(pageName, new Set<string>());
  }
  const pageKeys = pendingKeys.get(pageName);
  if (!pageKeys) return;
  if (pageKeys.has(key)) return; // already queued

  pageKeys.add(key);
  scheduleFlush();
};

const useTranslationHook = () => {
  const { tolgeeTranslationKeys, isLoadingTranslations }: any = useSelector(
    ({ appData }: AppState) => appData,
  );

  const clientTrackedKeys = useRef(new Set<string>());
  const hasLoggedSummary = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      clientTrackedKeys.current.clear();
      hasLoggedSummary.current = false;
    }
  }, [typeof window !== "undefined" ? window.location.pathname : ""]);

  useEffect(() => {
    if (typeof window !== "undefined" && !hasLoggedSummary.current) {
      const logSummary = () => {
        if (clientTrackedKeys.current.size > 0) {
          hasLoggedSummary.current = true;
        }
      };

      const timer = setTimeout(logSummary, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [tolgeeTranslationKeys]);

  const t = useCallback(
    (key: string, _value?: Record<string, string>, options?: { dynamic?: boolean }) => {
      try {
        const isDynamic = options?.dynamic ?? false;

        if (typeof window === "undefined") {
          trackKeyUsage(key, isDynamic);
        } else {
          trackKeyUsage(key, isDynamic);
          if (key && typeof key === "string") {
            clientTrackedKeys.current.add(key);
          }
        }

        // Return empty string while translations are loading
        if (isLoadingTranslations) {
          return "";
        }

        const langObj = tolgeeTranslationKeys;

        if (!key || typeof key !== "string") {
          return key || "";
        }

        const resolvedValue = langObj ? langObj[key] : undefined;

        // Report missing critical keys to Sentry (only dot-formatted, non-dynamic)
        if (resolvedValue === undefined && !isDynamic && !isLoadingTranslations) {
          const pageName = typeof window !== "undefined" ? window.location.pathname : "server";
          reportMissingKey(key, pageName);
        }

        const translated = resolvedValue !== undefined ? resolvedValue : key;

        if (typeof translated === "string" && _value) {
          return Object.keys(_value).reduce(
            (str, k) => str.replace(new RegExp(`{${k}}`, "g"), _value[k]),
            translated,
          );
        }

        return translated;
      } catch (error) {
        return key || "";
      }
    },
    [tolgeeTranslationKeys, isLoadingTranslations],
  );

  return { t, isLoadingTranslations };
};

export default useTranslationHook;
